import type { KnowledgeParticle } from '../../../types/domain';
import {
  isPasDeliberatePracticeStream,
  type LearningEpisodeStream,
  type SubjectCode,
} from '../../../utils/subjectConfig';
import { getSubjectValueBindingRecord } from '../../../utils/subjectValueBinding';
import { extractStructuredTextParts } from '../../../utils/textHelper';
import {
  buildParticleTitle,
  buildRevisionFamilyCards,
  type RevisionFamilyDeckCard,
} from '../reviewDeckBuilder';
import {
  readRevisionPrintEmbeddedImageRecord,
  type RevisionPrintParticle,
} from './RevisionPrintAction';
import { resolveRevisionPrintSourceOrigin } from './printParticleOrigin';

function readMetadataText(metadata: Record<string, unknown> | undefined, keys: string[]) {
  if (!metadata) return '';
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

/** Compact spine path for print; empty when unbound. */
export function resolveParticleSpineLabel(metadata: Record<string, unknown> | undefined | null): string {
  const meta = metadata || undefined;
  const binding = getSubjectValueBindingRecord(meta);
  const spineNodeTitle = readMetadataText(binding, ['spineNodeTitle', 'candidateSpineNodeTitle', 'title', 'spineTitle'])
    || readMetadataText(meta, ['spineNodeTitle', 'candidateSpineNodeTitle']);
  const moduleLabel = readMetadataText(binding, ['moduleLabel', 'moduleName'])
    || readMetadataText(binding, ['moduleKey', 'module'])
    || readMetadataText(meta, ['moduleLabel', 'moduleKey']);
  return [moduleLabel, spineNodeTitle].filter(Boolean).join(' / ');
}

function joinTextParts(main: string, items: string[]) {
  return [main, ...items].map((part) => part.trim()).filter(Boolean).join('\n');
}

function normalizePrintTextKey(value: string) {
  return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function buildParticleQuestionLines(particle: KnowledgeParticle) {
  const parts = extractStructuredTextParts(particle.questions, 'question');
  return { main: parts.main, items: parts.items };
}

function buildParticleAnswerLines(particle: KnowledgeParticle) {
  const parts = extractStructuredTextParts(particle.answers, 'answer');
  return { main: parts.main, items: parts.items };
}

function buildRevisionFamilyPreviewCard(
  particle: KnowledgeParticle,
  index: number,
  stream: LearningEpisodeStream,
): RevisionFamilyDeckCard {
  const cards = buildRevisionFamilyCards(particle, index, stream);
  return cards[0] || {
    id: `${particle.id}:preview`,
    particleId: particle.id,
    title: buildParticleTitle(particle, index),
    questionMain: buildParticleTitle(particle, index),
    questionItems: [],
    answerMain: '',
    answerItems: [],
    topicKey: 'preview',
    topicLabel: 'preview',
    subjectLabel: String(particle.subject || '').trim() || '未分学科',
  };
}

/**
 * Print sub-stems must come from the particle's structured Q/A baseline.
 * Stream decorations like `ignore-sub-questions` (flashcard_drill) strip deck
 * sub-cards for study UX — they must not zero out print / chip counts.
 */
function buildPrintSubCardsFromParticle(
  particle: KnowledgeParticle,
  questionMain: string,
): Array<{ question: string; answer: string }> {
  const question = buildParticleQuestionLines(particle);
  const answer = buildParticleAnswerLines(particle);
  const seenQuestionKeys = new Set([normalizePrintTextKey(questionMain)]);
  return question.items
    .filter(Boolean)
    .map((subQuestion, subIndex) => ({
      question: subQuestion,
      answer: answer.items[subIndex] || '',
    }))
    .filter((card) => {
      const key = normalizePrintTextKey(card.question);
      if (!key || seenQuestionKeys.has(key)) return false;
      seenQuestionKeys.add(key);
      return true;
    });
}

/**
 * Adapt Mobile ReviewSessionScreen `buildRevisionPrintParticle` for WebApp2 deck cards.
 */
export function buildRevisionPrintParticleFromDeck(
  particle: KnowledgeParticle,
  index: number,
  stream: LearningEpisodeStream,
  cardsForParticle: RevisionFamilyDeckCard[],
  subject?: SubjectCode,
): RevisionPrintParticle {
  const previewCard = cardsForParticle[0] || buildRevisionFamilyPreviewCard(particle, index, stream);
  const isPasDeliberatePractice = isPasDeliberatePracticeStream(stream);
  const metadata = particle.metadata as Record<string, unknown> | undefined;
  const sourceOrigin = resolveRevisionPrintSourceOrigin(metadata, particle.tags);
  const questionLines = buildParticleQuestionLines(particle);
  const questionMain = (
    previewCard.sharedProblemContent
    || questionLines.main
    || previewCard.questionMain
    || buildParticleTitle(particle, index)
  );

  // Deliberate practice worksheet: one stem per KP (no expandable sub-list chrome).
  if (isPasDeliberatePractice) {
    const fallbackAnswer = buildParticleAnswerLines(particle);
    const fullAnswerCard = cardsForParticle.find((card) => card.cardKind === 'full-answer');
    return {
      id: particle.id,
      title: previewCard.sharedProblemTitle || previewCard.familyLabel || previewCard.title || buildParticleTitle(particle, index),
      questionMain,
      spineLabel: resolveParticleSpineLabel(metadata) || null,
      sourceOrigin,
      embeddedImageRecord: readRevisionPrintEmbeddedImageRecord(metadata),
      embeddedImage: null,
      answerMain: fallbackAnswer.main || '',
      fullAnswer: fullAnswerCard?.answerMain || joinTextParts(fallbackAnswer.main, fallbackAnswer.items),
      subQuestions: [],
      subQuestionsExpanded: false,
      subCards: [],
      familySummary: previewCard.familySummary || null,
    };
  }

  const subCards = buildPrintSubCardsFromParticle(particle, questionMain);
  const fullAnswerCard = cardsForParticle.find((card) => card.cardKind === 'full-answer');
  const fallbackAnswer = buildParticleAnswerLines(particle);
  const answerMain = fallbackAnswer.main || '';
  const fullAnswer = fullAnswerCard?.answerMain || joinTextParts(fallbackAnswer.main, fallbackAnswer.items);

  return {
    id: particle.id,
    title: previewCard.sharedProblemTitle || previewCard.familyLabel || previewCard.title || buildParticleTitle(particle, index),
    questionMain,
    spineLabel: resolveParticleSpineLabel(metadata) || null,
    sourceOrigin,
    embeddedImageRecord: readRevisionPrintEmbeddedImageRecord(metadata),
    embeddedImage: null,
    answerMain,
    fullAnswer,
    subQuestions: subCards.map((card) => card.question).filter(Boolean),
    // Math: default collapse sub-stems in print preview; other subjects stay expanded.
    subQuestionsExpanded: subject !== 'math',
    subCards,
    familySummary: previewCard.familySummary || null,
  };
}

/** Build print particles for an ordered KP list using contiguous family deck cards. */
export function buildRevisionPrintParticlesFromDeck(
  particles: KnowledgeParticle[],
  stream: LearningEpisodeStream,
  subject?: SubjectCode,
): RevisionPrintParticle[] {
  return particles.map((particle, index) => {
    const cardsForParticle = buildRevisionFamilyCards(particle, index, stream);
    return buildRevisionPrintParticleFromDeck(particle, index, stream, cardsForParticle, subject);
  });
}
